import { useRef, useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import type {
  TeamResponse,
  ProspectResponse,
  TeamCoachResponse,
  TeamCoachPayload,
  BulkAddProspectsToTeamResponse,
  TeamUploadResponse,
} from "../../../../types/api";
import { updateTeam, bulkAddProspectsToTeam } from "../../../../services/teams";
import { getTeamUploads, uploadTeamFile } from "../../../../services/teamUploads";
import { getProspects, createProspect } from "../../../../services/prospects";
import {
  getTeamCoach,
  createTeamCoach,
  updateTeamCoach,
  deleteTeamCoach,
} from "../../../../services/team-coaches";
import { formatPhoneNumber, stripPhoneFormat } from "../../../../utils/formatters";
import { HandleInput } from "../../../../components/HandleInput";
import "./TeamEditorModal.css";

// Modal state types
type ModalState =
  | { type: "actions" }
  | { type: "deleteConfirmation" }
  | { type: "renameTeam" }
  | { type: "addExistingPlayer" }
  | { type: "createNewPlayer" }
  | { type: "coachContact" }
  | { type: "coachContactForm" }
  | { type: "uploadSchedule" }
  | { type: "viewSchedule" };

/**
 * Classify an upload for inline preview. PDFs and images render directly in
 * the site; anything else (CSV, Excel, .ics) can't be previewed in-browser, so
 * we offer a download/open action instead of a broken embed.
 */
type SchedulePreviewKind = "pdf" | "image" | "unsupported";

const getSchedulePreviewKind = (
  upload: TeamUploadResponse
): SchedulePreviewKind => {
  const type = (upload.contentType || "").toLowerCase();
  const name = (upload.fileName || "").toLowerCase();
  if (type === "application/pdf" || name.endsWith(".pdf")) return "pdf";
  // Extensions mirror SCHEDULE_ACCEPT's image types (png/jpg/jpeg).
  if (type.startsWith("image/") || /\.(png|jpe?g)$/.test(name)) {
    return "image";
  }
  return "unsupported";
};

/** File types accepted for a team schedule upload. */
const SCHEDULE_ACCEPT = ".pdf,.png,.jpg,.jpeg,.csv,.xls,.xlsx,.ics";

interface CoachForm {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  twitterHandle: string;
}

const EMPTY_COACH_FORM: CoachForm = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  twitterHandle: "",
};

const coachToForm = (c: TeamCoachResponse): CoachForm => ({
  firstName: c.firstName,
  lastName: c.lastName,
  email: c.email ?? "",
  phone: formatPhoneNumber(c.phone ?? ""),
  twitterHandle: c.twitterHandle ?? "",
});

interface TeamEditorModalProps {
  team: TeamResponse;
  isOpen: boolean;
  onClose: () => void;
  onRemove: (teamId: string) => void;
  /** Close every team tab at once. Touches no team data. */
  onCloseAll: () => void;
  onDelete: (teamId: string) => void;
  onTeamUpdate: () => void;
}

interface FormData {
  firstName: string;
  lastName: string;
  jerseyNumber: string;
  gradYear: string;
  position: string;
}

interface FormErrors {
  firstName?: string;
  lastName?: string;
  jerseyNumber?: string;
}

export const TeamEditorModal = ({
  team,
  isOpen,
  onClose,
  onRemove,
  onCloseAll,
  onDelete,
  onTeamUpdate,
}: TeamEditorModalProps) => {
  const navigate = useNavigate();
  const modalRef = useRef<HTMLDivElement>(null);
  const renameInputRef = useRef<HTMLInputElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const firstNameInputRef = useRef<HTMLInputElement>(null);
  const scheduleFileInputRef = useRef<HTMLInputElement>(null);

  // Modal state
  const [modalState, setModalState] = useState<ModalState>({ type: "actions" });

  // Rename team state
  const [teamName, setTeamName] = useState("");
  const [renameError, setRenameError] = useState<string | null>(null);
  const [renameSaving, setRenameSaving] = useState(false);

  // Add existing player state
  const [searchTerm, setSearchTerm] = useState("");
  const [prospects, setProspects] = useState<ProspectResponse[]>([]);
  const [searching, setSearching] = useState(false);
  const [selectedProspects, setSelectedProspects] = useState<ProspectResponse[]>(
    []
  );
  const [submittingAdd, setSubmittingAdd] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  const [addSummary, setAddSummary] =
    useState<BulkAddProspectsToTeamResponse | null>(null);

  // Create new player state
  const [formData, setFormData] = useState<FormData>({
    firstName: "",
    lastName: "",
    jerseyNumber: "",
    gradYear: "",
    position: "",
  });
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [createSaving, setCreateSaving] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  // Coach contact state
  const [coach, setCoach] = useState<TeamCoachResponse | null>(null);
  const [coachLoading, setCoachLoading] = useState(false);
  const [coachSaving, setCoachSaving] = useState(false);
  const [coachRemoving, setCoachRemoving] = useState(false);
  const [coachError, setCoachError] = useState<string | null>(null);
  const [coachForm, setCoachForm] = useState<CoachForm>(EMPTY_COACH_FORM);

  // Schedule uploads state
  const [schedules, setSchedules] = useState<TeamUploadResponse[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState<string | null>(null);

  // Load the team's schedule uploads so we know whether to show "View Schedule".
  const loadSchedules = useCallback(async () => {
    try {
      const uploads = await getTeamUploads(team.id);
      setSchedules(uploads);
    } catch {
      // Non-fatal: a failed list just hides "View Schedule". Upload errors are
      // surfaced separately via uploadError.
      setSchedules([]);
    }
  }, [team.id]);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setModalState({ type: "actions" });
      loadSchedules();
    } else {
      // Reset all state when modal closes
      setTeamName("");
      setRenameError(null);
      setRenameSaving(false);
      setSearchTerm("");
      setProspects([]);
      setSearching(false);
      setSelectedProspects([]);
      setSubmittingAdd(false);
      setAddError(null);
      setAddSummary(null);
      setFormData({
        firstName: "",
        lastName: "",
        jerseyNumber: "",
        gradYear: "",
        position: "",
      });
      setFormErrors({});
      setCreateSaving(false);
      setCreateError(null);
      setCoach(null);
      setCoachLoading(false);
      setCoachSaving(false);
      setCoachRemoving(false);
      setCoachError(null);
      setCoachForm(EMPTY_COACH_FORM);
      setSchedules([]);
      setUploading(false);
      setUploadError(null);
      setUploadSuccess(null);
    }
  }, [isOpen, loadSchedules]);

  // Auto-focus inputs when views change
  useEffect(() => {
    if (!isOpen) return;

    if (modalState.type === "renameTeam") {
      setTeamName(team.name);
      setTimeout(() => renameInputRef.current?.focus(), 100);
    } else if (modalState.type === "addExistingPlayer") {
      setSelectedProspects([]);
      setAddSummary(null);
      setAddError(null);
      setSearchTerm("");
      setProspects([]);
      setTimeout(() => searchInputRef.current?.focus(), 100);
    } else if (modalState.type === "createNewPlayer") {
      setTimeout(() => firstNameInputRef.current?.focus(), 100);
    }
  }, [modalState.type, isOpen, team.name]);

  // Debounced search for add existing player
  useEffect(() => {
    if (modalState.type !== "addExistingPlayer") return;

    const timer = setTimeout(async () => {
      if (searchTerm.trim()) {
        setSearching(true);
        setAddError(null);
        try {
          const results = await getProspects({ searchTerm });
          // Filter out prospects already on this team
          const filtered = results.items.filter(
            (p) => !p.teams.some((t) => t.id === team.id)
          );
          setProspects(filtered);
        } catch (error) {
          setAddError(
            error instanceof Error ? error.message : "Failed to search prospects"
          );
        } finally {
          setSearching(false);
        }
      } else {
        setProspects([]);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm, modalState.type, team.id]);

  // Load coach contact info when entering the coach contact view
  const loadCoach = useCallback(async () => {
    setCoachLoading(true);
    setCoachError(null);
    try {
      const data = await getTeamCoach(team.id);
      setCoach(data);
      setCoachForm(data ? coachToForm(data) : EMPTY_COACH_FORM);
    } catch (error) {
      setCoachError(
        error instanceof Error ? error.message : "Failed to load coach info"
      );
    } finally {
      setCoachLoading(false);
    }
  }, [team.id]);

  useEffect(() => {
    if (!isOpen) return;
    if (modalState.type === "coachContact") {
      loadCoach();
    }
  }, [isOpen, modalState.type, loadCoach]);

  // Close modal when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Don't close if currently saving
      if (renameSaving || submittingAdd || createSaving || coachSaving || coachRemoving || uploading) return;

      if (
        modalRef.current &&
        !modalRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose, renameSaving, submittingAdd, createSaving, coachSaving, coachRemoving, uploading]);

  // ESC key to close
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      // Don't close if currently saving
      if (renameSaving || submittingAdd || createSaving || coachSaving || coachRemoving || uploading) return;

      if (e.key === "Escape" && isOpen) {
        if (modalState.type !== "actions") {
          // Go back to actions view
          setModalState({ type: "actions" });
        } else {
          onClose();
        }
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen, onClose, modalState.type, renameSaving, submittingAdd, createSaving, coachSaving, coachRemoving, uploading]);

  // Handlers
  const handleCloseTab = () => {
    onRemove(team.id);
    onClose();
  };

  const handleCloseAllTabs = () => {
    onCloseAll();
    onClose();
  };

  const handleViewRoster = () => {
    const params = new URLSearchParams({ teamId: team.id, teamName: team.name });
    onClose();
    navigate(`/rosters/roster-images?${params.toString()}`);
  };

  const handleDeleteClick = () => {
    setModalState({ type: "deleteConfirmation" });
  };

  const handleViewSchedule = () => {
    const latest = schedules[0];
    if (!latest) return;
    // View the schedule inline within the site rather than opening a new tab.
    setModalState({ type: "viewSchedule" });
  };

  const handleScheduleFileChosen = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = e.target.files?.[0];
    // Reset the input so choosing the same file again re-triggers onChange.
    e.target.value = "";
    if (!file) return;

    setUploading(true);
    setUploadError(null);
    setUploadSuccess(null);

    try {
      await uploadTeamFile(team.id, file);
      await loadSchedules();
      setUploadSuccess(`Uploaded "${file.name}".`);
    } catch (error) {
      setUploadError(
        error instanceof Error ? error.message : "Failed to upload schedule"
      );
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteConfirm = () => {
    onDelete(team.id);
    onClose();
  };

  const handleRenameTeam = async () => {
    const trimmedName = teamName.trim();

    if (!trimmedName) {
      setRenameError("Team name is required");
      return;
    }

    if (trimmedName === team.name) {
      // No change, just close
      onClose();
      return;
    }

    setRenameSaving(true);
    setRenameError(null);

    try {
      await updateTeam(team.id, { name: trimmedName });
      onTeamUpdate();
      onClose();
    } catch (error) {
      setRenameError(
        error instanceof Error ? error.message : "Failed to rename team"
      );
    } finally {
      setRenameSaving(false);
    }
  };

  const addToSelection = (prospect: ProspectResponse) => {
    if (submittingAdd) return;
    setSelectedProspects((prev) =>
      prev.some((p) => p.id === prospect.id) ? prev : [...prev, prospect]
    );
  };

  const removeFromSelection = (prospectId: string) => {
    if (submittingAdd) return;
    setSelectedProspects((prev) => prev.filter((p) => p.id !== prospectId));
  };

  const handleAddSelectedProspects = async () => {
    if (selectedProspects.length === 0) return;

    setSubmittingAdd(true);
    setAddError(null);

    try {
      const result = await bulkAddProspectsToTeam(
        team.id,
        selectedProspects.map((p) => p.id)
      );
      setAddSummary(result);
      onTeamUpdate();
    } catch (error) {
      setAddError(
        error instanceof Error ? error.message : "Failed to add players"
      );
    } finally {
      setSubmittingAdd(false);
    }
  };

  const validateCreateForm = (): boolean => {
    const errors: FormErrors = {};

    if (!formData.firstName.trim()) {
      errors.firstName = "First name is required";
    }

    if (!formData.lastName.trim()) {
      errors.lastName = "Last name is required";
    }

    if (!formData.jerseyNumber.trim()) {
      errors.jerseyNumber = "Jersey number is required";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSaveCoach = async () => {
    const firstName = coachForm.firstName.trim();
    const lastName = coachForm.lastName.trim();
    const email = coachForm.email.trim();
    const phoneDigits = stripPhoneFormat(coachForm.phone);
    const twitterHandle = coachForm.twitterHandle.trim();

    if (!firstName || !lastName) {
      setCoachError("First and last name are required");
      return;
    }
    if (!email && !phoneDigits && !twitterHandle) {
      setCoachError("Enter at least one of: email, phone, or Twitter handle");
      return;
    }

    const payload: TeamCoachPayload = {
      firstName,
      lastName,
      email: email || null,
      phone: phoneDigits || null,
      twitterHandle: twitterHandle || null,
    };

    setCoachSaving(true);
    setCoachError(null);
    try {
      const result = coach
        ? await updateTeamCoach(team.id, payload)
        : await createTeamCoach(team.id, payload);
      setCoach(result);
      setCoachForm(coachToForm(result));
      setModalState({ type: "coachContact" });
    } catch (error) {
      setCoachError(
        error instanceof Error ? error.message : "Failed to save coach info"
      );
    } finally {
      setCoachSaving(false);
    }
  };

  const handleRemoveCoach = async () => {
    if (!coach) return;
    const confirmed = window.confirm(
      `Remove coach contact info for ${team.name}?`
    );
    if (!confirmed) return;

    setCoachRemoving(true);
    setCoachError(null);
    try {
      await deleteTeamCoach(team.id);
      setCoach(null);
      setCoachForm(EMPTY_COACH_FORM);
      setModalState({ type: "coachContactForm" });
    } catch (error) {
      setCoachError(
        error instanceof Error ? error.message : "Failed to remove coach info"
      );
    } finally {
      setCoachRemoving(false);
    }
  };

  const handleCreatePlayer = async () => {
    if (!validateCreateForm()) return;

    setCreateSaving(true);
    setCreateError(null);

    try {
      await createProspect({
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        jerseyNumber: parseInt(formData.jerseyNumber.trim(), 10),
        teams: [team.id],
        gradYear: formData.gradYear
          ? parseInt(formData.gradYear, 10)
          : undefined,
        position: formData.position.trim() || undefined,
      });

      onTeamUpdate();
      onClose();
    } catch (error) {
      setCreateError(
        error instanceof Error ? error.message : "Failed to create player"
      );
    } finally {
      setCreateSaving(false);
    }
  };

  // Render functions
  const renderActionsView = () => (
    <div className="team-chips__modal-content">
      <button className="team-chips__action-button" onClick={handleCloseTab}>
        <svg
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          className="team-chips__action-icon"
        >
          <path
            d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16ZM7.5 10h5"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        <span>Close Tab</span>
      </button>

      <button
        type="button"
        className="team-chips__action-button"
        onClick={handleCloseAllTabs}
        data-testid="team-editor-close-all-tabs"
      >
        <svg
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          className="team-chips__action-icon"
          aria-hidden="true"
        >
          <path
            d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16ZM7.5 7.5l5 5M12.5 7.5l-5 5"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        <span>Close All Tabs</span>
      </button>

      <button
        className="team-chips__action-button"
        onClick={() => setModalState({ type: "renameTeam" })}
      >
        <svg
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          className="team-chips__action-icon"
        >
          <path
            d="M14.166 2.5a2.357 2.357 0 0 1 3.334 3.334L6.25 17.083l-4.583.833.833-4.583L14.166 2.5Z"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        <span>Rename Team</span>
      </button>

      <button className="team-chips__action-button" onClick={handleViewRoster}>
        <svg
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          className="team-chips__action-icon"
        >
          <path
            d="M2.5 5a1.667 1.667 0 0 1 1.667-1.667h11.666A1.667 1.667 0 0 1 17.5 5v10a1.667 1.667 0 0 1-1.667 1.667H4.167A1.667 1.667 0 0 1 2.5 15V5Zm0 8.333 4.167-4.166a1.667 1.667 0 0 1 2.357 0L15 15M12.917 7.083h.008"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        <span>View Roster</span>
      </button>

      <button
        className="team-chips__action-button"
        onClick={() => setModalState({ type: "addExistingPlayer" })}
      >
        <svg
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          className="team-chips__action-icon"
        >
          <path
            d="M13.333 17.5v-1.667a3.333 3.333 0 0 0-3.333-3.333H4.167a3.333 3.333 0 0 0-3.334 3.333V17.5M15.833 6.667v5M18.333 9.167h-5M6.667 9.167A3.333 3.333 0 1 0 6.667 2.5a3.333 3.333 0 0 0 0 6.667Z"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        <span>Add Existing Players</span>
      </button>

      <button
        className="team-chips__action-button"
        onClick={() => setModalState({ type: "createNewPlayer" })}
      >
        <svg
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          className="team-chips__action-icon"
        >
          <path
            d="M10 18.333a8.333 8.333 0 1 0 0-16.666 8.333 8.333 0 0 0 0 16.666ZM10 6.667V13.333M6.667 10h6.666"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        <span>Create New Player</span>
      </button>

      <button
        className="team-chips__action-button"
        onClick={() => setModalState({ type: "coachContact" })}
      >
        <svg
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          className="team-chips__action-icon"
        >
          <path
            d="M14.167 17.5v-1.667A3.333 3.333 0 0 0 10.833 12.5H5.833A3.333 3.333 0 0 0 2.5 15.833V17.5M16.667 6.667h2.5m-1.25-1.25v2.5M8.333 9.167a3.333 3.333 0 1 0 0-6.667 3.333 3.333 0 0 0 0 6.667Z"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        <span>Coach Contact Info</span>
      </button>

      <button
        className="team-chips__action-button"
        onClick={() => setModalState({ type: "uploadSchedule" })}
        data-testid="upload-schedule-action"
      >
        <svg
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          className="team-chips__action-icon"
        >
          <path
            d="M17.5 12.5v3.333A1.667 1.667 0 0 1 15.833 17.5H4.167A1.667 1.667 0 0 1 2.5 15.833V12.5M14.167 6.667 10 2.5 5.833 6.667M10 2.5v10"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        <span>Upload Schedule</span>
      </button>

      {schedules.length > 0 && (
        <button
          className="team-chips__action-button"
          onClick={handleViewSchedule}
          data-testid="view-schedule-action"
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            className="team-chips__action-icon"
          >
            <path
              d="M11.667 1.667H5A1.667 1.667 0 0 0 3.333 3.333v13.334A1.667 1.667 0 0 0 5 18.333h10a1.667 1.667 0 0 0 1.667-1.666V6.667l-5-5Z"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M11.667 1.667v5h5M6.667 10.833h6.666M6.667 14.167h6.666"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <span>View Schedule</span>
        </button>
      )}

      <button
        className="team-chips__action-button team-chips__action-button--danger"
        onClick={handleDeleteClick}
      >
        <svg
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          className="team-chips__action-icon"
        >
          <path
            d="M2.5 5h15M8.333 9.167v5M11.667 9.167v5M3.333 5l.834 10a1.667 1.667 0 0 0 1.666 1.667h8.334a1.667 1.667 0 0 0 1.666-1.667l.834-10M7.5 5V3.333A1.333 1.333 0 0 1 8.833 2h2.334a1.333 1.333 0 0 1 1.333 1.333V5"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        <span>Delete Team</span>
      </button>
    </div>
  );

  const renderDeleteConfirmation = () => (
    <div className="team-chips__modal-content team-chips__modal-content--confirmation">
      <p className="team-chips__confirmation-message">
        Are you sure you want to delete "{team.name}"? This will NOT delete any
        prospects.
      </p>
      <div className="team-chips__confirmation-actions">
        <button
          className="team-chips__confirmation-button team-chips__confirmation-button--cancel"
          onClick={() => setModalState({ type: "actions" })}
        >
          Cancel
        </button>
        <button
          className="team-chips__confirmation-button team-chips__confirmation-button--delete"
          onClick={handleDeleteConfirm}
        >
          Delete
        </button>
      </div>
    </div>
  );

  const renderRenameTeamView = () => (
    <div className="team-chips__modal-content">
      <div className="team-chips__form-field">
        <label htmlFor="team-editor-modal-team-name-0" className="team-chips__form-label">Team Name</label>
        <input id="team-editor-modal-team-name-0"
          ref={renameInputRef}
          type="text"
          className={`team-chips__form-input ${
            renameError ? "team-chips__form-input--error" : ""
          }`}
          value={teamName}
          onChange={(e) => {
            setTeamName(e.target.value);
            setRenameError(null);
          }}
          placeholder="Enter team name"
          disabled={renameSaving}
          maxLength={255}
        />
        {renameError && (
          <span className="team-chips__form-error">{renameError}</span>
        )}
      </div>

      <div className="team-chips__form-footer">
        <button
          className="team-chips__form-button team-chips__form-button--cancel"
          onClick={() => setModalState({ type: "actions" })}
          disabled={renameSaving}
        >
          Cancel
        </button>
        <button
          className="team-chips__form-button team-chips__form-button--primary"
          onClick={handleRenameTeam}
          disabled={renameSaving}
        >
          {renameSaving ? "Saving..." : "Save"}
        </button>
      </div>
    </div>
  );

  const renderAddExistingPlayerView = () => {
    if (addSummary) {
      const { addedCount, alreadyOnTeamProspectIds, notFoundProspectIds } =
        addSummary;
      return (
        <div className="team-chips__modal-content">
          <div className="team-chips__bulk-summary">
            <p className="team-chips__bulk-summary-line">
              Added <strong>{addedCount}</strong>{" "}
              {addedCount === 1 ? "player" : "players"} to{" "}
              <strong>{team.name}</strong>.
            </p>
            {alreadyOnTeamProspectIds.length > 0 && (
              <p className="team-chips__bulk-summary-line team-chips__bulk-summary-line--muted">
                {alreadyOnTeamProspectIds.length}{" "}
                {alreadyOnTeamProspectIds.length === 1 ? "was" : "were"} already
                on the team.
              </p>
            )}
            {notFoundProspectIds.length > 0 && (
              <p className="team-chips__bulk-summary-line team-chips__bulk-summary-line--muted">
                {notFoundProspectIds.length} could not be found.
              </p>
            )}
          </div>

          <div className="team-chips__form-footer">
            <button
              type="button"
              className="team-chips__form-button team-chips__form-button--primary"
              onClick={onClose}
            >
              Done
            </button>
          </div>
        </div>
      );
    }

    const selectedCount = selectedProspects.length;
    const primaryLabel = submittingAdd
      ? "Adding..."
      : selectedCount === 0
        ? `Add players to ${team.name}`
        : `Add ${selectedCount} player${selectedCount === 1 ? "" : "s"} to ${team.name}`;
    const displayedProspects = prospects.filter(
      (p) => !selectedProspects.some((s) => s.id === p.id)
    );

    return (
      <div className="team-chips__modal-content team-chips__modal-content--add-existing">
        <div className="team-chips__add-body">
          <div className="team-chips__search-container">
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              className="team-chips__search-icon"
            >
              <path
                d="M9.167 15.833a6.667 6.667 0 1 0 0-13.333 6.667 6.667 0 0 0 0 13.333ZM17.5 17.5l-3.625-3.625"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <input
              ref={searchInputRef}
              type="text"
              className="team-chips__search-input"
              placeholder="Search players..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              disabled={submittingAdd}
            />
            {searchTerm && (
              <button
                className="team-chips__search-clear"
                onClick={() => setSearchTerm("")}
                disabled={submittingAdd}
              >
                ×
              </button>
            )}
          </div>

          {addError && (
            <div className="team-chips__form-error team-chips__form-error--banner">
              {addError}
            </div>
          )}

          {searching && (
            <div className="team-chips__loading">
              <div className="team-chips__spinner"></div>
            </div>
          )}

          {!searching && !searchTerm.trim() && (
            <div className="team-chips__empty-state">
              Search for players to add
            </div>
          )}

          {!searching &&
            searchTerm.trim() &&
            displayedProspects.length === 0 && (
              <div className="team-chips__empty-state">
                {prospects.length === 0
                  ? "No players found. Try a different search term."
                  : "All matching players are already in your selection."}
              </div>
            )}

          {!searching && displayedProspects.length > 0 && (
            <div className="team-chips__prospect-list">
              {displayedProspects.map((prospect) => (
                <button
                  key={prospect.id}
                  type="button"
                  className="team-chips__prospect-item"
                  onClick={() => addToSelection(prospect)}
                  disabled={submittingAdd}
                  aria-label={`Add ${prospect.firstName} ${prospect.lastName} to selection`}
                >
                  <div className="team-chips__prospect-info">
                    <div className="team-chips__prospect-name">
                      {prospect.firstName} {prospect.lastName}
                    </div>
                    <div className="team-chips__prospect-details">
                      {prospect.position && `${prospect.position} • `}
                      {prospect.teams.map((t) => t.name).join(", ")}
                    </div>
                  </div>
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    className="team-chips__prospect-add-icon"
                    aria-hidden="true"
                  >
                    <circle cx="12" cy="12" r="10" fill="currentColor" />
                    <path
                      d="M12 8v8M8 12h8"
                      stroke="#fff"
                      strokeWidth="1.75"
                      strokeLinecap="round"
                    />
                  </svg>
                </button>
              ))}
            </div>
          )}

        </div>

        <div className="team-chips__add-footer">
          {selectedProspects.length > 0 && (
            <div className="team-chips__selected-section">
              {selectedProspects.map((prospect) => (
                <div
                  key={prospect.id}
                  className="team-chips__selected-chip"
                >
                  <span className="team-chips__selected-chip-name">
                    {prospect.firstName} {prospect.lastName}
                  </span>
                  <button
                    type="button"
                    className="team-chips__selected-chip-remove"
                    onClick={() => removeFromSelection(prospect.id)}
                    disabled={submittingAdd}
                    aria-label={`Remove ${prospect.firstName} ${prospect.lastName}`}
                  >
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 14 14"
                      fill="none"
                    >
                      <path
                        d="M3 3l8 8M11 3l-8 8"
                        stroke="currentColor"
                        strokeWidth="1.75"
                        strokeLinecap="round"
                      />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="team-chips__form-footer team-chips__form-footer--bulk">
            <button
              className="team-chips__form-button team-chips__form-button--cancel"
              onClick={() => setModalState({ type: "actions" })}
              disabled={submittingAdd}
            >
              Back
            </button>
            <button
              type="button"
              className="team-chips__form-button team-chips__form-button--primary"
              onClick={handleAddSelectedProspects}
              disabled={selectedCount === 0 || submittingAdd}
            >
              {primaryLabel}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderCreateNewPlayerView = () => (
    <div className="team-chips__modal-content">
      {createError && (
        <div className="team-chips__form-error team-chips__form-error--banner">
          {createError}
        </div>
      )}

      <div className="team-chips__form-field">
        <label htmlFor="team-editor-modal-first-name-1" className="team-chips__form-label">First Name *</label>
        <input id="team-editor-modal-first-name-1"
          ref={firstNameInputRef}
          type="text"
          className={`team-chips__form-input ${
            formErrors.firstName ? "team-chips__form-input--error" : ""
          }`}
          value={formData.firstName}
          onChange={(e) => {
            setFormData({ ...formData, firstName: e.target.value });
            setFormErrors({ ...formErrors, firstName: undefined });
          }}
          placeholder="First Name"
          disabled={createSaving}
          style={{ textTransform: "capitalize" }}
        />
        {formErrors.firstName && (
          <span className="team-chips__form-error">{formErrors.firstName}</span>
        )}
      </div>

      <div className="team-chips__form-field">
        <label htmlFor="team-editor-modal-last-name-2" className="team-chips__form-label">Last Name *</label>
        <input id="team-editor-modal-last-name-2"
          type="text"
          className={`team-chips__form-input ${
            formErrors.lastName ? "team-chips__form-input--error" : ""
          }`}
          value={formData.lastName}
          onChange={(e) => {
            setFormData({ ...formData, lastName: e.target.value });
            setFormErrors({ ...formErrors, lastName: undefined });
          }}
          placeholder="Last Name"
          disabled={createSaving}
          style={{ textTransform: "capitalize" }}
        />
        {formErrors.lastName && (
          <span className="team-chips__form-error">{formErrors.lastName}</span>
        )}
      </div>

      <div className="team-chips__form-field">
        <label htmlFor="team-editor-modal-jersey-number-3" className="team-chips__form-label">Jersey Number *</label>
        <input id="team-editor-modal-jersey-number-3"
          type="number"
          className={`team-chips__form-input ${
            formErrors.jerseyNumber ? "team-chips__form-input--error" : ""
          }`}
          value={formData.jerseyNumber}
          onChange={(e) => {
            setFormData({ ...formData, jerseyNumber: e.target.value });
            setFormErrors({ ...formErrors, jerseyNumber: undefined });
          }}
          placeholder="Number"
          disabled={createSaving}
        />
        {formErrors.jerseyNumber && (
          <span className="team-chips__form-error">
            {formErrors.jerseyNumber}
          </span>
        )}
      </div>

      <div className="team-chips__form-field">
        <label htmlFor="team-editor-modal-grad-year-4" className="team-chips__form-label">Grad Year</label>
        <input id="team-editor-modal-grad-year-4"
          type="number"
          className="team-chips__form-input"
          value={formData.gradYear}
          onChange={(e) =>
            setFormData({ ...formData, gradYear: e.target.value })
          }
          placeholder="2025"
          disabled={createSaving}
        />
      </div>

      <div className="team-chips__form-field">
        <label htmlFor="team-editor-modal-position-5" className="team-chips__form-label">Position</label>
        <input id="team-editor-modal-position-5"
          type="text"
          className="team-chips__form-input"
          value={formData.position}
          onChange={(e) =>
            setFormData({ ...formData, position: e.target.value })
          }
          placeholder="Position"
          disabled={createSaving}
          style={{ textTransform: "uppercase" }}
        />
      </div>

      <div className="team-chips__form-footer">
        <button
          className="team-chips__form-button team-chips__form-button--cancel"
          onClick={() => setModalState({ type: "actions" })}
          disabled={createSaving}
        >
          Cancel
        </button>
        <button
          className="team-chips__form-button team-chips__form-button--primary"
          onClick={handleCreatePlayer}
          disabled={createSaving}
        >
          {createSaving ? "Saving..." : "Save"}
        </button>
      </div>
    </div>
  );

  const renderCoachContactView = () => (
    <div className="team-chips__modal-content">
      <div className="team-chips__coach-scroll">
        {coachError && (
          <div className="team-chips__form-error team-chips__form-error--banner">
            {coachError}
          </div>
        )}

        {coachLoading ? (
          <div className="team-chips__loading">
            <div className="team-chips__spinner" />
          </div>
        ) : coach ? (
          <>
            <div className="team-chips__coach-name">
              {coach.firstName} {coach.lastName}
            </div>

            <div className="team-chips__coach-actions">
              {coach.email && (
                <a
                  className="team-chips__coach-action-button"
                  href={`mailto:${coach.email}`}
                  aria-label={`Email ${coach.firstName} ${coach.lastName}`}
                >
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path
                      d="M2.5 5.833A1.667 1.667 0 0 1 4.167 4.167h11.666A1.667 1.667 0 0 1 17.5 5.833v8.334a1.667 1.667 0 0 1-1.667 1.666H4.167A1.667 1.667 0 0 1 2.5 14.167V5.833Zm0 0L10 11.667l7.5-5.834"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <span>Email</span>
                </a>
              )}
              {coach.phone && (
                <a
                  className="team-chips__coach-action-button"
                  href={`tel:${coach.phone}`}
                  aria-label={`Call ${coach.firstName} ${coach.lastName}`}
                >
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path
                      d="M17.5 14.1v2.5a1.667 1.667 0 0 1-1.817 1.667 16.475 16.475 0 0 1-7.183-2.558 16.233 16.233 0 0 1-5-5 16.475 16.475 0 0 1-2.558-7.217A1.667 1.667 0 0 1 2.6 1.667h2.5a1.667 1.667 0 0 1 1.667 1.433 10.7 10.7 0 0 0 .583 2.342 1.667 1.667 0 0 1-.375 1.758L5.917 8.258a13.333 13.333 0 0 0 5 5l1.058-1.058a1.667 1.667 0 0 1 1.758-.375 10.7 10.7 0 0 0 2.342.583A1.667 1.667 0 0 1 17.5 14.1Z"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <span>Call</span>
                </a>
              )}
              {coach.twitterHandle && (
                <a
                  className="team-chips__coach-action-button"
                  href={`https://x.com/${coach.twitterHandle}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`Open @${coach.twitterHandle} on X`}
                >
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path
                      d="M15.833 2.5h2.917l-6.375 7.283L20 17.5h-5.875l-4.6-6.017L4.25 17.5H1.333l6.817-7.792L1 2.5h6.025l4.158 5.5L15.833 2.5Z"
                      fill="currentColor"
                    />
                  </svg>
                  <span>Twitter</span>
                </a>
              )}
            </div>

            <div className="team-chips__coach-details">
              {coach.email && (
                <div className="team-chips__coach-detail-row">
                  <span className="team-chips__coach-detail-label">Email</span>
                  <span className="team-chips__coach-detail-value">{coach.email}</span>
                </div>
              )}
              {coach.phone && (
                <div className="team-chips__coach-detail-row">
                  <span className="team-chips__coach-detail-label">Phone</span>
                  <span className="team-chips__coach-detail-value">{formatPhoneNumber(coach.phone)}</span>
                </div>
              )}
              {coach.twitterHandle && (
                <div className="team-chips__coach-detail-row">
                  <span className="team-chips__coach-detail-label">Twitter</span>
                  <span className="team-chips__coach-detail-value">@{coach.twitterHandle}</span>
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="team-chips__empty-state">
            No coach contact info yet for this team.
          </div>
        )}
      </div>

      {!coachLoading && (
        <div className="team-chips__form-footer">
          {coach ? (
            <>
              <button
                className="team-chips__form-button team-chips__form-button--cancel"
                onClick={handleRemoveCoach}
                disabled={coachRemoving || coachSaving}
              >
                {coachRemoving ? "Removing..." : "Remove"}
              </button>
              <button
                className="team-chips__form-button team-chips__form-button--primary"
                onClick={() => setModalState({ type: "coachContactForm" })}
                disabled={coachRemoving || coachSaving}
              >
                Edit
              </button>
            </>
          ) : (
            <button
              className="team-chips__form-button team-chips__form-button--primary"
              onClick={() => setModalState({ type: "coachContactForm" })}
            >
              Add Coach Info
            </button>
          )}
        </div>
      )}
    </div>
  );

  const renderCoachContactFormView = () => (
    <div className="team-chips__modal-content">
      <div className="team-chips__coach-scroll">
        {coachError && (
          <div className="team-chips__form-error team-chips__form-error--banner">
            {coachError}
          </div>
        )}

        <div className="team-chips__form-field">
          <label htmlFor="team-editor-modal-first-name-6" className="team-chips__form-label">First Name *</label>
        <input id="team-editor-modal-first-name-6"
          type="text"
          className="team-chips__form-input"
          value={coachForm.firstName}
          onChange={(e) => {
            setCoachForm({ ...coachForm, firstName: e.target.value });
            setCoachError(null);
          }}
          placeholder="First name"
          disabled={coachSaving}
          autoFocus
        />
      </div>

      <div className="team-chips__form-field">
        <label htmlFor="team-editor-modal-last-name-7" className="team-chips__form-label">Last Name *</label>
        <input id="team-editor-modal-last-name-7"
          type="text"
          className="team-chips__form-input"
          value={coachForm.lastName}
          onChange={(e) => {
            setCoachForm({ ...coachForm, lastName: e.target.value });
            setCoachError(null);
          }}
          placeholder="Last name"
          disabled={coachSaving}
        />
      </div>

      <div className="team-chips__form-field">
        <label htmlFor="team-editor-modal-email-8" className="team-chips__form-label">Email</label>
        <input id="team-editor-modal-email-8"
          type="email"
          className="team-chips__form-input"
          value={coachForm.email}
          onChange={(e) => {
            setCoachForm({ ...coachForm, email: e.target.value });
            setCoachError(null);
          }}
          placeholder="coach@example.com"
          disabled={coachSaving}
        />
      </div>

      <div className="team-chips__form-field">
        <label htmlFor="team-editor-modal-phone-9" className="team-chips__form-label">Phone</label>
        <input id="team-editor-modal-phone-9"
          type="tel"
          className="team-chips__form-input"
          value={coachForm.phone}
          onChange={(e) => {
            setCoachForm({ ...coachForm, phone: formatPhoneNumber(e.target.value) });
            setCoachError(null);
          }}
          placeholder="(555) 123-4567"
          disabled={coachSaving}
        />
      </div>

      <div className="team-chips__form-field">
        <HandleInput
          label="Twitter Handle"
          value={coachForm.twitterHandle}
          onChange={(v) => {
            setCoachForm({ ...coachForm, twitterHandle: v });
            setCoachError(null);
          }}
          placeholder="handle"
          variant="dashboard"
          disabled={coachSaving}
        />
      </div>

        <p className="team-chips__coach-helper">
          At least one of email, phone, or Twitter handle is required.
        </p>
      </div>

      <div className="team-chips__form-footer">
        <button
          className="team-chips__form-button team-chips__form-button--cancel"
          onClick={() => {
            if (coach) {
              setCoachForm(coachToForm(coach));
              setCoachError(null);
              setModalState({ type: "coachContact" });
            } else {
              setModalState({ type: "actions" });
            }
          }}
          disabled={coachSaving}
        >
          Cancel
        </button>
        <button
          className="team-chips__form-button team-chips__form-button--primary"
          onClick={handleSaveCoach}
          disabled={coachSaving}
        >
          {coachSaving ? "Saving..." : "Save"}
        </button>
      </div>
    </div>
  );

  const renderUploadScheduleView = () => {
    const latest = schedules[0];
    return (
      <div className="team-chips__modal-content">
        {uploadError && (
          <div
            className="team-chips__form-error team-chips__form-error--banner"
            role="alert"
            aria-live="assertive"
          >
            {uploadError}
          </div>
        )}

        {uploadSuccess && (
          <div
            className="team-chips__upload-success"
            role="status"
            aria-live="polite"
          >
            {uploadSuccess}
          </div>
        )}

        <p className="team-chips__upload-helper">
          Upload a schedule file for <strong>{team.name}</strong>. Accepted types:
          PDF, image, CSV, Excel, or calendar (.ics).
        </p>

        <input
          ref={scheduleFileInputRef}
          type="file"
          accept={SCHEDULE_ACCEPT}
          onChange={handleScheduleFileChosen}
          disabled={uploading}
          style={{ display: "none" }}
          data-testid="schedule-file-input"
        />

        <button
          type="button"
          className="team-chips__form-button team-chips__form-button--primary"
          onClick={() => scheduleFileInputRef.current?.click()}
          disabled={uploading}
          data-testid="choose-schedule-file"
        >
          {uploading ? "Uploading..." : "Choose File"}
        </button>

        {uploading && (
          <div className="team-chips__loading">
            <div className="team-chips__spinner" />
          </div>
        )}

        {latest && !uploading && (
          <button
            type="button"
            className="team-chips__form-button team-chips__form-button--cancel"
            onClick={handleViewSchedule}
            data-testid="upload-view-latest"
          >
            View latest ({latest.fileName})
          </button>
        )}
      </div>
    );
  };

  const renderViewScheduleView = () => {
    const latest = schedules[0];
    if (!latest) {
      return (
        <div className="team-chips__modal-content">
          <div className="team-chips__empty-state">
            No schedule available to view.
          </div>
        </div>
      );
    }

    const kind = getSchedulePreviewKind(latest);

    return (
      <div className="team-chips__modal-content team-chips__schedule-viewer">
        <div className="team-chips__schedule-viewer-stage">
          {kind === "pdf" && (
            <iframe
              className="team-chips__schedule-frame"
              src={latest.downloadUrl}
              title={`Schedule: ${latest.fileName}`}
              data-testid="schedule-preview-frame"
            />
          )}
          {kind === "image" && (
            <img
              className="team-chips__schedule-image"
              src={latest.downloadUrl}
              alt={`Schedule: ${latest.fileName}`}
              data-testid="schedule-preview-image"
            />
          )}
          {kind === "unsupported" && (
            <div
              className="team-chips__schedule-unsupported"
              data-testid="schedule-preview-unsupported"
            >
              <p>
                This file type can't be previewed here. You can download{" "}
                <strong>{latest.fileName}</strong> to view it.
              </p>
            </div>
          )}
        </div>

        <div className="team-chips__form-footer">
          <a
            className="team-chips__form-button team-chips__form-button--cancel"
            href={latest.downloadUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="schedule-open-tab"
          >
            Open in new tab
          </a>
          <a
            className="team-chips__form-button team-chips__form-button--primary"
            href={latest.downloadUrl}
            download={latest.fileName}
            data-testid="schedule-download"
          >
            Download
          </a>
        </div>
      </div>
    );
  };

  const renderContent = () => {
    switch (modalState.type) {
      case "actions":
        return renderActionsView();
      case "deleteConfirmation":
        return renderDeleteConfirmation();
      case "renameTeam":
        return renderRenameTeamView();
      case "addExistingPlayer":
        return renderAddExistingPlayerView();
      case "createNewPlayer":
        return renderCreateNewPlayerView();
      case "coachContact":
        return renderCoachContactView();
      case "coachContactForm":
        return renderCoachContactFormView();
      case "uploadSchedule":
        return renderUploadScheduleView();
      case "viewSchedule":
        return renderViewScheduleView();
    }
  };

  const getHeaderTitle = () => {
    switch (modalState.type) {
      case "deleteConfirmation":
        return "Delete Team";
      case "renameTeam":
        return "Rename Team";
      case "addExistingPlayer":
        return "Add Existing Players";
      case "createNewPlayer":
        return "Add New Player";
      case "coachContact":
        return "Coach Contact Info";
      case "coachContactForm":
        return coach ? "Edit Coach Info" : "Add Coach Info";
      case "uploadSchedule":
        return "Upload Schedule";
      case "viewSchedule":
        return "View Schedule";
      default:
        return team.name;
    }
  };

  const getHeaderSubtitle = () => {
    if (
      modalState.type === "addExistingPlayer" ||
      modalState.type === "createNewPlayer"
    ) {
      return `to ${team.name}`;
    }
    if (
      modalState.type === "coachContact" ||
      modalState.type === "coachContactForm" ||
      modalState.type === "viewSchedule"
    ) {
      return team.name;
    }
    return null;
  };

  if (!isOpen) return null;

  const isCoachState =
    modalState.type === "coachContact" || modalState.type === "coachContactForm";
  const inAddSummary =
    modalState.type === "addExistingPlayer" && !!addSummary;
  const inAddExisting =
    modalState.type === "addExistingPlayer" && !addSummary;
  const isViewSchedule = modalState.type === "viewSchedule";
  const headerBusy =
    renameSaving || submittingAdd || createSaving || coachSaving || coachRemoving || uploading;

  return (
    <div className="team-chips__modal-overlay">
      <div
        className={`team-chips__modal${
          isCoachState ? " team-chips__modal--coach" : ""
        }${inAddExisting ? " team-chips__modal--add-existing" : ""}${
          isViewSchedule ? " team-chips__modal--view-schedule" : ""
        }`}
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="team-chips-modal-title"
      >
        <div className="team-chips__modal-header">
          <div className="team-chips__modal-header-left">
            {isCoachState && (
              <button
                type="button"
                className="team-chips__modal-back"
                onClick={() => setModalState({ type: "actions" })}
                disabled={headerBusy}
                aria-label="Back to actions"
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path
                    d="M12.5 15 7.5 10l5-5"
                    stroke="currentColor"
                    strokeWidth="1.75"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            )}
            <div>
              <h3 id="team-chips-modal-title">{getHeaderTitle()}</h3>
              {getHeaderSubtitle() && (
                <span className="team-chips__modal-subtitle">
                  {getHeaderSubtitle()}
                </span>
              )}
            </div>
          </div>
          <button
            className="team-chips__modal-close"
            onClick={
              isCoachState || modalState.type === "actions" || inAddSummary
                ? onClose
                : () => setModalState({ type: "actions" })
            }
            disabled={headerBusy}
          >
            ×
          </button>
        </div>

        {renderContent()}
      </div>
    </div>
  );
};
